
import { Select, SelectOption } from './Select'

export interface TimezoneSelectProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  minWidth?: string
  showSelectedIcon?: boolean
  variant?: 'full' | 'us-only'
  id?: string
}

// Flag icon components
const USFlagIcon = ({ className }: { className?: string }) => (
  <span className={`text-base leading-none flex items-center ${className}`}>🇺🇸</span>
)
const UKFlagIcon = ({ className }: { className?: string }) => (
  <span className={`text-base leading-none flex items-center ${className}`}>🇬🇧</span>
)
const FranceFlagIcon = ({ className }: { className?: string }) => (
  <span className={`text-base leading-none flex items-center ${className}`}>🇫🇷</span>
)
const JapanFlagIcon = ({ className }: { className?: string }) => (
  <span className={`text-base leading-none flex items-center ${className}`}>🇯🇵</span>
)
const ChinaFlagIcon = ({ className }: { className?: string }) => (
  <span className={`text-base leading-none flex items-center ${className}`}>🇨🇳</span>
)
const AustraliaFlagIcon = ({ className }: { className?: string }) => (
  <span className={`text-base leading-none flex items-center ${className}`}>🇦🇺</span>
)
const GermanyFlagIcon = ({ className }: { className?: string }) => (
  <span className={`text-base leading-none flex items-center ${className}`}>🇩🇪</span>
)
const IndiaFlagIcon = ({ className }: { className?: string }) => (
  <span className={`text-base leading-none flex items-center ${className}`}>🇮🇳</span>
)

const GlobeIcon = ({ className }: { className?: string }) => (
  <span className={`text-base leading-none flex items-center ${className}`}>🌐</span>
)

// Full timezone options with comprehensive coverage
const fullTimezoneOptions: SelectOption[] = [
  // UTC — the system default (SYSTEM_DEFAULTS.timezone); must be selectable so the
  // default value renders as "UTC" rather than falling through to the placeholder.
  { value: 'UTC', label: 'UTC', icon: GlobeIcon },

  // US Timezones
  { value: 'America/New_York', label: 'Eastern Time (America/New_York)', icon: USFlagIcon },
  { value: 'America/Chicago', label: 'Central Time (America/Chicago)', icon: USFlagIcon },
  { value: 'America/Denver', label: 'Mountain Time (America/Denver)', icon: USFlagIcon },
  { value: 'America/Los_Angeles', label: 'Pacific Time (America/Los_Angeles)', icon: USFlagIcon },
  
  // Europe
  { value: 'Europe/London', label: 'GMT (Europe/London)', icon: UKFlagIcon },
  { value: 'Europe/Paris', label: 'CET (Europe/Paris)', icon: FranceFlagIcon },
  { value: 'Europe/Berlin', label: 'CET (Europe/Berlin)', icon: GermanyFlagIcon },
  
  // Asia Pacific
  { value: 'Asia/Tokyo', label: 'JST (Asia/Tokyo)', icon: JapanFlagIcon },
  { value: 'Asia/Shanghai', label: 'China Standard Time (Asia/Shanghai)', icon: ChinaFlagIcon },
  { value: 'Asia/Kolkata', label: 'IST (Asia/Kolkata)', icon: IndiaFlagIcon },
  { value: 'Australia/Sydney', label: 'AEST (Australia/Sydney)', icon: AustraliaFlagIcon },
]

// US-only timezone options with friendly names
const usTimezoneOptions: SelectOption[] = [
  { value: 'America/Los_Angeles', label: 'Pacific Time', icon: USFlagIcon },
  { value: 'America/Denver', label: 'Mountain Time', icon: USFlagIcon },
  { value: 'America/Chicago', label: 'Central Time', icon: USFlagIcon },
  { value: 'America/New_York', label: 'Eastern Time', icon: USFlagIcon },
]

export function TimezoneSelect({
  value,
  onChange,
  placeholder = 'Select timezone',
  className,
  disabled = false,
  minWidth = '200px',
  showSelectedIcon = true,
  variant = 'full',
  id
}: TimezoneSelectProps) {
  const options = variant === 'us-only' ? usTimezoneOptions : fullTimezoneOptions

  return (
    <Select
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      className={className}
      disabled={disabled}
      minWidth={minWidth}
      showSelectedIcon={showSelectedIcon}
      id={id}
    />
  )
} 